import { type FC, memo, useEffect, useRef } from 'react';

import { Box, BoxProps } from '@mui/joy';

import Prism from 'ui/Prism';

import usePrepareHtml from '../model/usePrepareHtml';

export interface HtmlReaderProps extends BoxProps {
  html?: string;
}

const HtmlReader: FC<HtmlReaderProps> = (props) => {
  const { html, ...rest } = props;

  const prismRef = useRef<any>();

  const preparedHtml = usePrepareHtml(html);

  useEffect(() => {
    const { current: prism } = prismRef;
    if (!preparedHtml?.length || !prism) {
      return;
    }
    prism.highlightAll();
  }, [preparedHtml]);

  return (
    <Box display="flex" flexDirection="column" {...rest}>
      <Prism ref={prismRef} />
      {!!preparedHtml && (
        <Box
          className="html-container"
          component="div"
          dangerouslySetInnerHTML={{ __html: preparedHtml || '' }}
          sx={{
            '&': {
              lineHeight: 1.5,
              color: 'var(--joy-palette-text-primary)',
            },
            '& a': {
              '&': {
                color: 'inherit',
                fontWeight: 500,
              },
              '&:hover': {
                textDecoration: 'none',
              },
            },
            '& h1': {
              fontWeight: 500,
              fontSize: 28,
              margin: '1em 0 0',
            },
            '& h2': {
              fontWeight: 500,
              fontSize: 24,
              margin: '1em 0 0',
            },
            '& h3': {
              fontWeight: 500,
              fontSize: 20,
              margin: '1em 0 0',
            },
            '& h4': {
              fontWeight: 500,
              fontSize: 18,
              margin: '1em 0 0',
            },
            '& h5': {
              fontWeight: 500,
              fontSize: 16,
              margin: '1em 0 0',
            },
            '& p': {
              margin: '1.25em 0 0',
            },
            '& strong, & b': {
              fontWeight: 500,
              margin: '1em 0 0',
            },
            '& img': {
              '&': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '0.25em',
                margin: '1em 0 0',
              },
              '&:not([width])': {
                width: '100%',
              },
            },
            '& video': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '0.25em',
              margin: '1em 0 0',
            },
            '& figure': {
              margin: 0,
            },
            '& blockquote': {
              '&': {
                fontSize: '1.125em',
                margin: '1em 0 0',
                paddingLeft: '1.5em',
                paddingTop: '0.5em',
                paddingBottom: '0.5em',
                borderLeft: '0.25em solid var(--joy-palette-highlight-borderColor)',
                color: 'var(--joy-palette-text-primary)',
              },
              '& p': {
                margin: 0,
              },
            },
            '& hr': {
              border: 0,
              borderBottom: '1px solid var(--joy-palette-neutral-softHoverBg)',
              marginTop: '3em',
              marginBottom: '3em',
            },
            '& pre': {
              '&': {
                position: 'relative',
                counterReset: 'line',
                padding: '0.5em',
                margin: '1em 0 0',
                fontSize: '0.875',
                lineHeight: '1.42857143',
                color: 'var(--joy-palette-neutral-softColor)',
                wordBreak: 'break-all',
                wordWrap: 'break-word',
                backgroundColor: 'var(--joy-palette-neutral-softBg)',
                borderRadius: '0.2857em',
                // whiteSpace: 'pre',
                whiteSpace: 'normal',
                overflow: 'auto',
              },
              '& code': {
                padding: '0',
              },
              '& code a': {
                color: 'inherit',
              },
            },
            '& code': {
              backgroundColor: 'var(--joy-palette-neutral-softBg)',
              borderRadius: '0.25em',
              padding: '0.25em 0.5em',
            },
            '& ul, & ol': {
              '&': {
                margin: 0,
              },
              '& li': {
                margin: '1em 0 0',
              },
            },
            '& iframe': {
              border: 'none',
            },
          }}
        />
      )}
    </Box>
  );
};

export default memo(HtmlReader);
